import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem("mv_token") || sessionStorage.getItem("mv_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setPermissions(data.permissions || {});
    } catch {
      localStorage.removeItem("mv_token");
      sessionStorage.removeItem("mv_token");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  const login = async (email, password, remember) => {
    const { data } = await api.post("/auth/login", { email, password, remember: !!remember });
    localStorage.removeItem("mv_token");
    sessionStorage.removeItem("mv_token");
    (remember ? localStorage : sessionStorage).setItem("mv_token", data.token);
    setUser(data.user);
    setPermissions(data.permissions || {});
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    localStorage.removeItem("mv_token");
    sessionStorage.removeItem("mv_token");
    setUser(null);
    setPermissions({});
  };

  const can = useCallback(
    (module, action = "view") => (permissions?.[module] || []).includes(action),
    [permissions]
  );

  const value = useMemo(
    () => ({ user, setUser, permissions, loading, login, logout, can, isAdmin: user?.role === "admin" }),
    [user, permissions, loading, can]
  );

  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
};
